import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { getShareArrivalChannel, clearShareArrivalChannel, trackShareBannerEvent } from '@/lib/analytics';

const CHANNEL_LABELS: Record<string, { label: string; color: string; bgColor: string }> = {
  whatsapp: { label: 'WhatsApp', color: 'text-[#25D366]', bgColor: 'bg-[#25D366]/10 border-[#25D366]/20' },
  twitter: { label: 'Twitter', color: 'text-[#1DA1F2]', bgColor: 'bg-[#1DA1F2]/10 border-[#1DA1F2]/20' },
  facebook: { label: 'Facebook', color: 'text-[#4267B2]', bgColor: 'bg-[#4267B2]/10 border-[#4267B2]/20' },
  linkedin: { label: 'LinkedIn', color: 'text-[#0A66C2]', bgColor: 'bg-[#0A66C2]/10 border-[#0A66C2]/20' },
  copy_link: { label: 'a friend', color: 'text-primary-400', bgColor: 'bg-primary-500/10 border-primary-500/20' },
  native: { label: 'a friend', color: 'text-primary-400', bgColor: 'bg-primary-500/10 border-primary-500/20' },
};

function getChannelMeta(channel: string) {
  return CHANNEL_LABELS[channel] || { label: 'a friend', color: 'text-primary-400', bgColor: 'bg-primary-500/10 border-primary-500/20' };
}

// WhatsApp icon inline (matches ShareBar)
function ChannelIcon({ channel }: { channel: string }) {
  if (channel === 'whatsapp') {
    return (
      <svg className="h-4 w-4 shrink-0 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    );
  }
  // Generic share icon for other channels
  return (
    <svg className="h-4 w-4 shrink-0 text-primary-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

/** GROW-001: Share Referral Welcome Banner
 *  Shows a subtle, dismissible banner when a user arrives via a shared link.
 *  Completes the viral loop: share -> arrival -> welcome -> engagement.
 *  Auto-dismisses after 8 seconds. */
export function ShareArrivalBanner() {
  const [channel, setChannel] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Small delay so the page settles before showing banner
    const timer = setTimeout(() => {
      const ch = getShareArrivalChannel();
      if (ch) {
        setChannel(ch);
        setVisible(true);
        trackShareBannerEvent('impression', ch);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => dismiss(), 8000);
    return () => clearTimeout(timer);
  }, [visible]);

  const dismiss = () => {
    setExiting(true);
    if (channel) {
      trackShareBannerEvent('dismiss', channel);
      clearShareArrivalChannel();
    }
    setTimeout(() => setVisible(false), 300);
  };

  const handleExplore = () => {
    if (channel) trackShareBannerEvent('cta_click', channel);
    clearShareArrivalChannel();
    setExiting(true);
    setTimeout(() => setVisible(false), 300);
  };

  if (!visible || !channel) return null;

  const meta = getChannelMeta(channel);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-16 left-1/2 z-50 -translate-x-1/2 transition-all duration-300 ${
        exiting ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'
      }`}
    >
      <div className={`flex items-center gap-3 rounded-2xl border ${meta.bgColor} backdrop-blur-md px-4 py-2.5 shadow-lg shadow-black/10`}>
        <ChannelIcon channel={channel} />
        <p className="text-sm text-text-primary">
          Shared via <span className={`font-semibold ${meta.color}`}>{meta.label}</span>
          <span className="hidden sm:inline text-text-secondary"> — welcome to MotoWalla</span>
        </p>
        <a
          href="/"
          onClick={handleExplore}
          className="ml-1 rounded-lg bg-primary-500/20 px-3 py-1 text-xs font-semibold text-primary-400 transition-colors hover:bg-primary-500/30"
        >
          Explore
        </a>
        <button
          onClick={dismiss}
          className="ml-1 rounded-lg p-1 text-text-tertiary transition-colors hover:bg-white/10 hover:text-text-secondary"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
